import { useEffect, useState, useCallback, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError, runAiJob } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { StatusBadge } from "../components/ui-helpers";
import { docTypeLabel } from "../lib/constants";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Checkbox } from "../components/ui/checkbox";
import { ArrowLeft, Plus, Trash, Sparkle, FloppyDisk, FilePdf, FileDoc, ShieldCheck, CircleNotch } from "@phosphor-icons/react";
import { GroundingBadge, GroundingReport } from "../components/GroundingReport";
import ApprovalWorkflow from "../components/ApprovalWorkflow";
import { useModuleVisibility } from "../hooks/useModuleVisibility";
import BulkApplyWizard from "../components/BulkApplyWizard";
const FLOW = {
  draft: [["review", "Submit for Review"]],
  review: [["approved", "Approve"], ["changes_requested", "Request Changes"]],
  changes_requested: [["review", "Resubmit"]],
  approved: [["published", "Publish"]],
  published: [],
};

function SectionRefEditor({ refs, reqs, canEdit, onChange, text = "", title = "" }) {
  const [std, setStd] = useState("");
  const [reqId, setReqId] = useState("");
  const [parts, setParts] = useState([]);
  const [sugs, setSugs] = useState(null);
  const [loadingSug, setLoadingSug] = useState(false);
  const standards = useMemo(() => {
    const s = [...new Set(reqs.map((r) => r.standard_id).filter(Boolean))];
    s.sort((a, b) => {
      const ac = a.toUpperCase().startsWith("CIP"), bc = b.toUpperCase().startsWith("CIP");
      if (ac !== bc) return ac ? -1 : 1;
      return a.localeCompare(b);
    });
    return s;
  }, [reqs]);
  const stdReqs = useMemo(
    () => reqs.filter((r) => r.standard_id === std)
      .sort((a, b) => (a.req_number || "").localeCompare(b.req_number || "", undefined, { numeric: true })),
    [reqs, std]
  );
  const curReq = reqs.find((r) => r.id === reqId);
  const availParts = (curReq?.sub_requirements || []).map((sp) => sp.label).filter(Boolean);
  const togglePart = (p) => setParts((ps) => (ps.includes(p) ? ps.filter((x) => x !== p) : [...ps, p]));
  const list = refs || [];

  const add = () => {
    if (!std || !curReq) return;
    onChange([...list, { standard_id: std, req_number: curReq.req_number, requirement_id: curReq.id, parts: [...parts] }]);
    setStd(""); setReqId(""); setParts([]);
  };
  const remove = (idx) => onChange(list.filter((_, i) => i !== idx));
  const selClass = "border border-border rounded-none text-sm h-8 px-2 bg-background disabled:opacity-60";

  const suggest = async () => {
    if ((text || "").trim().length < 15) { toast.error("Add some section content first, then try Suggest."); return; }
    setLoadingSug(true);
    try {
      const { data } = await api.post("/documents/suggest-refs", { text, title });
      const have = new Set(list.map((r) => `${r.standard_id}|${r.req_number}`));
      const fresh = (data.suggestions || []).filter((s) => !have.has(`${s.standard_id}|${s.req_number}`));
      setSugs(fresh);
      if (fresh.length === 0) toast.info("No new tag suggestions for this section.");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "AI suggestion failed.");
    } finally { setLoadingSug(false); }
  };
  const acceptSug = (s) => {
    onChange([...list, { standard_id: s.standard_id, req_number: s.req_number, requirement_id: s.requirement_id, parts: s.parts || [] }]);
    setSugs((cur) => (cur || []).filter((x) => !(x.standard_id === s.standard_id && x.req_number === s.req_number)));
  };

  return (
    <div data-testid="section-refs">
      {list.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mb-2">
          {list.map((r, idx) => (
            <span key={idx} data-testid={`section-ref-chip-${idx}`} className="inline-flex items-center gap-1 border border-indigo-300 bg-indigo-50 text-indigo-800 px-2 py-0.5 text-xs font-mono">
              {r.standard_id} {r.req_number}{(r.parts || []).length ? ` · ${(r.parts).length === 1 ? "part" : "parts"} ${r.parts.join(", ")}` : ""}
              {canEdit && <button type="button" onClick={() => remove(idx)} className="text-indigo-500 hover:text-red-600 font-bold" aria-label="remove">×</button>}
            </span>
          ))}
        </div>
      )}
      {canEdit && (
        <div className="flex flex-wrap items-center gap-2">
          <select data-testid="ref-standard-select" value={std} onChange={(e) => { setStd(e.target.value); setReqId(""); setParts([]); }} className={selClass}>
            <option value="">Standard…</option>
            {standards.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <select data-testid="ref-requirement-select" value={reqId} disabled={!std} onChange={(e) => { setReqId(e.target.value); setParts([]); }} className={selClass}>
            <option value="">Requirement…</option>
            {stdReqs.map((r) => <option key={r.id} value={r.id}>{r.req_number}</option>)}
          </select>
          {availParts.length > 0 && (
            <div className="flex flex-wrap items-center gap-1.5 border border-border px-2 py-1">
              <span className="text-[11px] text-muted-foreground">Parts:</span>
              {availParts.map((p) => (
                <label key={p} className="flex items-center gap-1 text-xs font-mono cursor-pointer">
                  <input type="checkbox" data-testid={`ref-part-${p}`} checked={parts.includes(p)} onChange={() => togglePart(p)} />
                  {p}
                </label>
              ))}
            </div>
          )}
          <button type="button" data-testid="add-section-ref-btn" onClick={add} disabled={!std || !reqId}
            className="flex items-center gap-1 border border-black px-2 py-1 text-xs font-semibold hover:bg-primary hover:text-white disabled:opacity-40">
            <Plus size={12} />Add
          </button>
          <button type="button" data-testid="suggest-refs-btn" onClick={suggest} disabled={loadingSug}
            className="flex items-center gap-1 border border-indigo-500 text-indigo-700 px-2 py-1 text-xs font-semibold hover:bg-indigo-600 hover:text-white disabled:opacity-40">
            <Sparkle size={12} weight="fill" />{loadingSug ? "Thinking…" : "Suggest (AI)"}
          </button>
        </div>
      )}
      {canEdit && sugs && sugs.length > 0 && (
        <div className="mt-2 border border-indigo-200 bg-indigo-50/60 p-2" data-testid="ref-suggestions">
          <div className="text-[11px] font-semibold uppercase text-indigo-700 mb-1.5 flex items-center gap-1"><Sparkle size={12} weight="fill" />AI suggestions — click to add</div>
          <div className="flex flex-wrap gap-1.5">
            {sugs.map((s, i) => (
              <button key={i} type="button" data-testid={`suggestion-${s.standard_id}-${s.req_number}`} onClick={() => acceptSug(s)}
                title={s.snippet} className="inline-flex items-center gap-1 border border-indigo-400 bg-white text-indigo-800 px-2 py-0.5 text-xs font-mono hover:bg-indigo-600 hover:text-white">
                <Plus size={11} />{s.standard_id} {s.req_number}{(s.parts || []).length ? ` · ${s.parts.join(", ")}` : ""}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default function DocumentEditor() {
  const { id } = useParams();
  const nav = useNavigate();
  const { canEdit: roleCanEdit, isRole, aiEnabled } = useAuth();
  const { isModuleHidden } = useModuleVisibility();
  const approvalsEnabled = !isModuleHidden("approvals");
  const [doc, setDoc] = useState(null);
  const [verifyBusy, setVerifyBusy] = useState(false);
  const [reqs, setReqs] = useState([]);
  const [reqFilter, setReqFilter] = useState("");
  const [inEffectOnly, setInEffectOnly] = useState(true);
  const [aiBusy, setAiBusy] = useState(false);
  const [exporting, setExporting] = useState("");
  const [bulk, setBulk] = useState(null);

  const load = useCallback(() => { api.get(`/documents/${id}`).then((r) => setDoc(r.data)); }, [id]);
  useEffect(() => { load(); api.get("/requirements").then((r) => setReqs(r.data)); }, [load]);

  const shownReqs = useMemo(
    () => reqs.filter((r) =>
      (!reqFilter || `${r.standard_id} ${r.req_number}`.toLowerCase().includes(reqFilter.toLowerCase())) &&
      (!inEffectOnly || r.status === "currently_enforceable")),
    [reqs, reqFilter, inEffectOnly]
  );
  const reqGroups = useMemo(() => {
    const rn = (r) => (r.req_number || "").replace(/[^0-9.]/g, "");
    const m = {};
    shownReqs.forEach((r) => { (m[r.standard_id] = m[r.standard_id] || []).push(r); });
    return Object.entries(m)
      .map(([std, items]) => [std, items.sort((a, b) => rn(a).localeCompare(rn(b), undefined, { numeric: true }))])
      .sort((a, b) => {
        const ac = a[0].toUpperCase().startsWith("CIP"), bc = b[0].toUpperCase().startsWith("CIP");
        if (ac !== bc) return ac ? -1 : 1;
        return a[0].localeCompare(b[0]);
      });
  }, [shownReqs]);

  if (!doc) return <div className="p-8 font-mono text-sm">Loading…</div>;
  const canEdit = roleCanEdit && !doc.locked;

  const setSection = (i, field, val) => {
    const sections = [...doc.sections];
    sections[i] = { ...sections[i], [field]: val };
    setDoc({ ...doc, sections });
  };
  const addSection = () => setDoc({ ...doc, sections: [...doc.sections, { id: crypto.randomUUID(), title: "New Section", content: "" }] });
  const removeSection = (i) => setDoc({ ...doc, sections: doc.sections.filter((_, idx) => idx !== i) });
  const toggleReq = (rid) => {
    const has = doc.linked_requirements.includes(rid);
    setDoc({ ...doc, linked_requirements: has ? doc.linked_requirements.filter((x) => x !== rid) : [...doc.linked_requirements, rid] });
  };

  const isCip = (r) => (r.standard_id || "").toUpperCase().startsWith("CIP");
  const inEffect = (r) => !inEffectOnly || r.status === "currently_enforceable";
  const setLinks = (ids, add) => {
    const set = new Set(doc.linked_requirements);
    ids.forEach((id) => (add ? set.add(id) : set.delete(id)));
    setDoc({ ...doc, linked_requirements: [...set] });
  };
  const toggleStandard = (std, add) => setLinks(reqs.filter((r) => r.standard_id === std).map((r) => r.id), add);
  const linkAllCip = () => setLinks(reqs.filter((r) => isCip(r) && inEffect(r)).map((r) => r.id), true);
  const clearAllLinks = () => setDoc({ ...doc, linked_requirements: [] });
  const linkAllShown = () => setLinks(shownReqs.map((r) => r.id), true);

  const save = async () => {
    try {
      const { data } = await api.put(`/documents/${id}`, {
        title: doc.title, doc_type: doc.doc_type, owner: doc.owner,
        sections: doc.sections, linked_requirements: doc.linked_requirements,
        guided_by: doc.guided_by || [], applicability_status: doc.applicability_status || null,
        applicability_reason: doc.applicability_reason || "",
      });
      setDoc(data); toast.success(`Saved as v${data.version}`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const changeStatus = async (status) => {
    try {
      const { data } = await api.post(`/documents/${id}/status`, { status });
      setDoc({ ...doc, status: data.status, approval_log: data.approval_log });
      toast.success(`Status: ${status}`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const exportDoc = async (fmt) => {    setExporting(fmt);
    try {
      const res = await api.get(`/documents/${id}/export?format=${fmt}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `${(doc.title || "document").replace(/[^A-Za-z0-9]+/g, "_")}.${fmt}`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Exported ${fmt.toUpperCase()}`);
    } catch (e) { toast.error("Export failed — please try again."); }
    finally { setExporting(""); }
  };

  const deleteDoc = async () => {
    if (!window.confirm(`Delete "${doc.title}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/documents/${id}`);
      toast.success("Document deleted");
      nav("/documents");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const aiDraft = async (i) => {
    setAiBusy(true);
    try {
      const data = await runAiJob("/ai/draft-document", { doc_type: doc.doc_type, topic: doc.sections[i].title });
      setSection(i, "content", data.content);
      toast.success("AI draft inserted");
    } catch (e) { toast.error("AI draft failed"); }
    finally { setAiBusy(false); }
  };

  const bulkTag = async () => {
    setAiBusy(true);
    try {
      const { data } = await api.post(`/documents/${id}/suggest-refs-bulk`);
      if (!data.sections || data.sections.length === 0) { toast.info("No new tag suggestions found across sections."); return; }
      const sel = {};
      data.sections.forEach((sec) => sec.suggestions.forEach((s) => { sel[`${sec.index}:${s.standard_id}:${s.req_number}`] = true; }));
      setBulk({ sections: data.sections, sel });
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "AI tagging failed.");
    } finally { setAiBusy(false); }
  };
  const toggleBulk = (k) => setBulk((b) => ({ ...b, sel: { ...b.sel, [k]: !b.sel[k] } }));
  const applyBulk = () => {
    const sections = doc.sections.map((s, idx) => {
      const sec = bulk.sections.find((x) => x.index === idx);
      if (!sec) return s;
      const have = new Set((s.refs || []).map((r) => `${r.standard_id}|${r.req_number}`));
      const add = sec.suggestions
        .filter((sg) => bulk.sel[`${idx}:${sg.standard_id}:${sg.req_number}`] && !have.has(`${sg.standard_id}|${sg.req_number}`))
        .map((sg) => ({ standard_id: sg.standard_id, req_number: sg.req_number, requirement_id: sg.requirement_id, parts: sg.parts || [] }));
      return add.length ? { ...s, refs: [...(s.refs || []), ...add] } : s;
    });
    const added = Object.values(bulk.sel).filter(Boolean).length;
    setDoc({ ...doc, sections });
    setBulk(null);
    toast.success(`Added ${added} tag${added === 1 ? "" : "s"} — review, then Save.`);
  };

  const verifyGrounding = async () => {
    setVerifyBusy(true);
    try {
      const { data } = await api.post(`/documents/${id}/verify-grounding`);
      setDoc((d) => ({ ...d, grounding_check: data }));
      toast.success(`Grounding checked — ${data.confidence}% confidence`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Grounding check failed");
    } finally { setVerifyBusy(false); }
  };

  const improveGrounding = async () => {
    setVerifyBusy(true);
    try {
      const { data } = await api.post(`/documents/${id}/improve-grounding`);
      setDoc((d) => ({ ...d, sections: data.sections, grounding_check: null }));
      toast.success("Document redrafted from the grounding findings — re-verify to score it");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not improve the document");
    } finally { setVerifyBusy(false); }
  };

  return (
    <div>
      <div className="border-b border-border px-8 py-5 bg-card sticky top-0 z-10 flex items-start justify-between">
        <div className="flex-1">
          <button onClick={() => nav("/documents")} className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-2"><ArrowLeft size={14} /> Documents</button>
          <Input data-testid="doc-title-edit" value={doc.title} disabled={!canEdit} onChange={(e) => setDoc({ ...doc, title: e.target.value })}
            className="rounded-none border-0 border-b-2 border-black font-display font-black text-2xl px-0 h-auto py-1 focus-visible:ring-0" />
          <div className="flex items-center gap-2 mt-2"><StatusBadge status={doc.status} /><span className="font-mono text-xs text-muted-foreground">v{doc.version} · {docTypeLabel(doc.doc_type)}</span>
            {doc.applicability_status && <span className={`border px-2 py-0.5 text-[11px] font-semibold uppercase ${doc.applicability_status === "applicable" ? "bg-green-50 text-green-700 border-green-300" : "bg-red-50 text-red-700 border-red-300"}`}>{doc.applicability_status.replace("_", " ")}</span>}
            {doc.guided_by?.length > 0 && <span className="text-[11px] text-muted-foreground" data-testid="guided-by">Guided by policy</span>}
            <GroundingBadge check={doc.grounding_check} testid="doc-grounding-badge" />
          </div>
        </div>
        <div className="flex gap-2">
          <Button data-testid="doc-export-pdf-btn" onClick={() => exportDoc("pdf")} disabled={!!exporting} variant="outline" className="rounded-none"><FilePdf size={16} className="mr-1" />{exporting === "pdf" ? "…" : "PDF"}</Button>
          <Button data-testid="doc-export-docx-btn" onClick={() => exportDoc("docx")} disabled={!!exporting} variant="outline" className="rounded-none"><FileDoc size={16} className="mr-1" />{exporting === "docx" ? "…" : "Word"}</Button>
          {canEdit && (
            <>
              <Button data-testid="save-doc-btn" onClick={save} variant="outline" className="rounded-none"><FloppyDisk size={16} className="mr-1" />Save</Button>
              <Button data-testid="doc-verify-grounding-btn" onClick={verifyGrounding} disabled={verifyBusy || !aiEnabled} variant="outline" className="rounded-none"
                title={!aiEnabled ? "AI features are turned off for this entity" : "Reverse-check this document against the NERC requirements it's linked to (1 AI credit)"}>
                {verifyBusy ? <CircleNotch size={16} className="mr-1 animate-spin" /> : <ShieldCheck size={16} className="mr-1" />}{verifyBusy ? "Checking…" : "Verify grounding"}
              </Button>
              {doc.grounding_check && (
                <Button data-testid="doc-improve-grounding-btn" onClick={improveGrounding} disabled={verifyBusy || !aiEnabled} variant="outline"
                  className="rounded-none border-emerald-300 bg-emerald-50 text-emerald-800 hover:bg-emerald-100"
                  title="Redraft this document using the grounding findings — corrects unsupported claims (1 AI credit)">
                  <Sparkle size={16} weight="fill" className="mr-1" />Improve with grounding
                </Button>
              )}
              <Button data-testid="bulk-tag-btn" onClick={bulkTag} disabled={aiBusy || !aiEnabled}
                title={!aiEnabled ? "AI features are turned off for this entity" : "AI-tag every section, then confirm"}
                className="rounded-none bg-indigo-600 hover:bg-indigo-700 text-white"><Sparkle size={16} weight="fill" className="mr-1" />{aiBusy ? "Tagging…" : "AI-tag all"}</Button>
              {(FLOW[doc.status] || []).map(([s, label]) => (
                <Button key={s} data-testid={`status-${s}-btn`} onClick={() => changeStatus(s)} className="rounded-none bg-primary hover:bg-primary/90">{label}</Button>
              ))}
            </>
          )}
          {isRole("admin", "analyst") && <BulkApplyWizard doc={doc} />}
          {isRole("admin") && (
            <Button data-testid="doc-delete-btn" onClick={deleteDoc} variant="outline"
              className="rounded-none border-red-300 text-red-600 hover:bg-red-50"><Trash size={16} className="mr-1" />Delete</Button>
          )}
        </div>
      </div>

      {doc.grounding_check && (
        <div className="px-8 pt-4 max-w-5xl" data-testid="doc-grounding-wrap">
          <GroundingReport check={doc.grounding_check} history={doc.grounding_history} testid="doc-grounding-report" />
        </div>
      )}

      {bulk && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="bulk-tag-modal">
          <div className="bg-card border border-border w-full max-w-2xl max-h-[85vh] flex flex-col">
            <div className="px-5 py-3 border-b border-border flex items-center justify-between">
              <h3 className="font-bold flex items-center gap-2"><Sparkle size={16} weight="fill" className="text-indigo-600" />Review AI tag suggestions</h3>
              <button onClick={() => setBulk(null)} className="text-muted-foreground hover:text-foreground">✕</button>
            </div>
            <div className="p-5 overflow-y-auto space-y-4">
              {bulk.sections.map((sec) => (
                <div key={sec.index} data-testid={`bulk-section-${sec.index}`}>
                  <div className="text-sm font-semibold mb-1.5">§{sec.index + 1} {sec.title}</div>
                  <div className="flex flex-wrap gap-1.5">
                    {sec.suggestions.map((sg) => {
                      const k = `${sec.index}:${sg.standard_id}:${sg.req_number}`;
                      const on = !!bulk.sel[k];
                      return (
                        <button key={k} type="button" onClick={() => toggleBulk(k)} title={sg.snippet}
                          data-testid={`bulk-sug-${sec.index}-${sg.standard_id}-${sg.req_number}`}
                          className={`inline-flex items-center gap-1 border px-2 py-0.5 text-xs font-mono ${on ? "border-indigo-500 bg-indigo-600 text-white" : "border-border bg-secondary/40 text-muted-foreground"}`}>
                          <span>{on ? "✓" : "+"}</span>{sg.standard_id} {sg.req_number}{(sg.parts || []).length ? ` · ${sg.parts.join(", ")}` : ""}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
            <div className="px-5 py-3 border-t border-border flex justify-end gap-2">
              <Button variant="outline" className="rounded-none" onClick={() => setBulk(null)}>Cancel</Button>
              <Button data-testid="bulk-apply-btn" className="rounded-none bg-indigo-600 hover:bg-indigo-700 text-white" onClick={applyBulk}>Apply selected</Button>
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 p-8">
        <div className="lg:col-span-3 space-y-4">
          {doc.sections.map((s, i) => (
            <div key={s.id} className="border border-border bg-card" data-testid={`section-${i}`}>
              <div className="flex items-center gap-2 border-b border-border px-3 py-2 bg-secondary/40">
                <Input value={s.title} disabled={!canEdit} onChange={(e) => setSection(i, "title", e.target.value)} className="rounded-none border-0 font-semibold bg-transparent px-1 focus-visible:ring-0" />
                {canEdit && <>
                  <button data-testid={`ai-draft-${i}`} onClick={() => aiDraft(i)} disabled={aiBusy || !aiEnabled} title={!aiEnabled ? "AI features are turned off for this entity" : undefined} className="flex items-center gap-1 border border-black px-2 py-1 text-xs font-semibold hover:bg-primary hover:text-white whitespace-nowrap disabled:opacity-50"><Sparkle size={12} weight="fill" />AI Draft</button>
                  <button onClick={() => removeSection(i)} className="text-muted-foreground hover:text-red-600"><Trash size={16} /></button>
                </>}
              </div>
              <Textarea value={s.content} disabled={!canEdit} onChange={(e) => setSection(i, "content", e.target.value)}
                placeholder="Write section content…" className="rounded-none border-0 min-h-[120px] focus-visible:ring-0 font-mono text-sm" />
              <div className="border-t border-border px-3 py-2 bg-secondary/20">
                <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground mb-1.5">Addresses — Standard · Requirement · Part(s)</div>
                <SectionRefEditor refs={s.refs} reqs={reqs} canEdit={canEdit} text={s.content} title={s.title} onChange={(refs) => setSection(i, "refs", refs)} />
              </div>
            </div>
          ))}
          {canEdit && <Button onClick={addSection} variant="outline" className="rounded-none"><Plus size={16} className="mr-1" />Add Section</Button>}
        </div>

        <div className="space-y-6">
          <div className="border border-border bg-card">
            <div className="border-b border-border px-4 py-3 flex items-center justify-between">
              <span className="font-display font-bold">Linked NERC Requirements</span>
              <span data-testid="linked-req-count" className="text-xs font-mono text-muted-foreground">{doc.linked_requirements.length} linked</span>
            </div>
            {canEdit && (
              <div className="px-4 py-3 border-b border-border space-y-2">
                <Input data-testid="req-filter-input" value={reqFilter} onChange={(e) => setReqFilter(e.target.value)}
                  placeholder="Filter standards… e.g. CIP, CIP-007" className="rounded-none h-8 text-sm" />
                <label className="flex items-center gap-2 text-xs cursor-pointer">
                  <Checkbox data-testid="in-effect-only" checked={inEffectOnly} onCheckedChange={(v) => setInEffectOnly(!!v)} className="rounded-none" />
                  In-effect only <span className="text-muted-foreground">(hide future / retired versions)</span>
                </label>
                <div className="flex flex-wrap gap-1.5">
                  <button data-testid="link-all-cip-btn" onClick={linkAllCip}
                    className="text-xs font-semibold border border-black bg-primary text-primary-foreground px-2 py-1 hover:bg-primary/90">Link all CIP{inEffectOnly ? " (in effect)" : ""}</button>
                  <button data-testid="link-all-shown-btn" onClick={linkAllShown}
                    className="text-xs font-semibold border border-border px-2 py-1 hover:bg-secondary">Link all shown</button>
                  <button data-testid="clear-all-links-btn" onClick={clearAllLinks}
                    className="text-xs font-semibold border border-border px-2 py-1 hover:bg-secondary text-red-600">Clear all</button>
                </div>
              </div>
            )}
            <div className="p-2 max-h-96 overflow-y-auto">
              {reqGroups.length === 0 && <div className="px-2 py-3 text-sm text-muted-foreground">No standards match “{reqFilter}”.</div>}
              {reqGroups.map(([std, items]) => {
                const linkedN = items.filter((r) => doc.linked_requirements.includes(r.id)).length;
                const allOn = linkedN === items.length;
                const state = allOn ? true : linkedN > 0 ? "indeterminate" : false;
                return (
                  <div key={std} className="mb-2">
                    <div className="flex items-center justify-between px-2 py-1.5 bg-secondary/60">
                      <label className="flex items-center gap-2 text-xs font-mono font-bold cursor-pointer">
                        <Checkbox data-testid={`link-std-${std}`} checked={state} disabled={!canEdit}
                          onCheckedChange={() => toggleStandard(std, !allOn)} className="rounded-none" />
                        {std}
                      </label>
                      <span className="text-[10px] text-muted-foreground font-mono">{linkedN}/{items.length}</span>
                    </div>
                    <div className="pl-3 pt-1 space-y-1">
                      {items.map((r) => (
                        <label key={r.id} className="flex items-start gap-2 text-sm cursor-pointer">
                          <Checkbox data-testid={`link-req-${r.standard_id}-${r.req_number}`} checked={doc.linked_requirements.includes(r.id)}
                            disabled={!canEdit} onCheckedChange={() => toggleReq(r.id)} className="mt-0.5 rounded-none" />
                          <span className="min-w-0">
                            <span className="font-mono font-bold">{r.req_number}</span>
                            {r.text && <span className="text-muted-foreground"> — {r.text.slice(0, 48)}{r.text.length > 48 ? "…" : ""}</span>}
                          </span>
                        </label>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {approvalsEnabled && <ApprovalWorkflow doc={doc} onChange={setDoc} />}

          {doc.approval_log?.length > 0 && (
            <div className="border border-border bg-card">
              <div className="border-b border-border px-4 py-3 font-display font-bold">Approval Trail</div>
              <div className="p-4 space-y-2 text-xs">
                {doc.approval_log.slice().reverse().map((l, i) => (
                  <div key={l.at ?? i} className="border-b border-border pb-2"><span className="font-semibold">{l.by}</span> · {l.from} → {l.to}<div className="text-muted-foreground font-mono">{l.at?.slice(0, 16).replace("T", " ")}</div></div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="mt-6 pt-4 border-t border-border text-center text-xs text-muted-foreground" data-testid="doc-claro-footer">
        Powered by Claro Compliance, LLC
      </div>
    </div>
  );
}
